import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box, Typography, Paper, Button, CircularProgress, Alert,
  IconButton, Tooltip, ToggleButton, ToggleButtonGroup,
  Dialog, DialogContent
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import SupportTicketDialog from '../../components/chat/SupportTicketDialog';
import InCallAssessmentMenu from '../../components/call/InCallAssessmentMenu';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import ArticleIcon from '@mui/icons-material/Article';
import EditNoteIcon from '@mui/icons-material/EditNote';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import axios from 'axios';
import Snackbar from '@mui/material/Snackbar';
import useLiveKitRoom from '../../hooks/useLiveKitRoom';
import useVideoSocket from '../../hooks/useVideoSocket';
import { useSocket } from '../../context/SocketContext';
import AssessmentRunner from '../../components/assessments/AssessmentRunner';
import VideoTile from '../../components/call/VideoTile';
import CallControls from '../../components/call/CallControls';
import TranscriptPane from '../../components/call/TranscriptPane';
import PreJoinLobby from '../../components/call/PreJoinLobby';
import ConnectionQualityBadge from '../../components/call/ConnectionQualityBadge';
import TranscriptReviewDialog from '../../components/call/TranscriptReviewDialog';
import DraggablePiP from '../../components/call/DraggablePiP';
import CallClinicalPanel from '../../components/call/CallClinicalPanel';
import CallNotesPane from '../../components/call/CallNotesPane';
import CallSummaryDialog from '../../components/call/CallSummaryDialog';
import EnrolmentApprovalPanel from '../../components/therapist/EnrolmentApprovalPanel';
import InSessionToolbar from '../../components/in-session/InSessionToolbar';
import { INCALL_BADGE_LABEL, INCALL_BADGE_TOOLTIP } from '../../components/call/consentCopy';
import { getCall, joinCall, endCall, recordCallConsent } from '../../services/callsApi';
import { Chip, Tooltip as MuiTooltip } from '@mui/material';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import { holdSessionAwake } from '../../services/sessionManager';

const LIVEKIT_URL = process.env.REACT_APP_LIVEKIT_URL;
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

function resolveRole(call, userId) {
  if (!call || !userId) return null;
  if (String(call.therapistId) === String(userId)) return 'therapist';
  if (String(call.clientId) === String(userId))   return 'client';
  return null;
}

function participantLabel(p) {
  if (!p) return '';
  return p.name || p.identity || '';
}

/**
 * Full-viewport video call room. Reused for both therapist and client; side
 * panels (clinical context / notes / transcript) render only for the therapist.
 */
export default function CallRoomPage({ token, user }) {
  // A live call keeps the session awake with no keyboard or mouse input (security batch 2).
  useEffect(() => holdSessionAwake('live_call'), []);
  const { liveCallId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const initialLiveKitToken = location.state?.livekitToken || null;

  const [call, setCall] = useState(null);
  const [livekitToken, setLivekitToken] = useState(initialLiveKitToken);
  const [loading, setLoading] = useState(true);
  const [joinInFlight, setJoinInFlight] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [postCall, setPostCall] = useState(null);
  const [micEnabled, setMicEnabled] = useState(true);
  const [cameraEnabled, setCameraEnabled] = useState(true);
  const [transcript, setTranscript] = useState([]);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [remoteWaitSeconds, setRemoteWaitSeconds] = useState(0);
  // AD-49: the client pressed Decline. Before this the therapist was never told.
  const [clientDeclined, setClientDeclined] = useState(false);

  // Layout state (therapist only)
  const [rightPanel, setRightPanel] = useState('transcript'); // 'transcript' | 'clinical' | 'notes' | 'none'
  const [pipVisible, setPipVisible] = useState(true);
  const [fullscreen, setFullscreen] = useState(false);

  const containerRef = useRef(null);

  const role = useMemo(() => resolveRole(call, user?._id || user?.id), [call, user]);
  const isTherapist = role === 'therapist';

  // AIMHI Direct: the enrolment decision (mark attended, approve, decline)
  // renders directly on the post-call screen below. EnrolmentApprovalPanel
  // returns null for a client without an enrolment, so other calls see no
  // change. (8-9 Sep 2026 tests: the panel had only lived inside the review
  // dialog, and the post-call screen did not even pass it the client.)

  const videoSocket = useVideoSocket(token);
  const mainSocket = useSocket();

  // Spec D — mid-call assessment delivery
  // Client side: receive assessment push, open runner.
  // Therapist side: receive results-ready, show toast.
  const [pendingAssessment, setPendingAssessment] = useState(null); // { assessmentKey, assessmentInstanceId }
  const [assessmentToast, setAssessmentToast] = useState(null);

  // Help / Report-issue dialog for the therapist — captures rich live-call
  // context so admins can correlate the report with LiveKit logs.
  const [supportTicketOpen, setSupportTicketOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const data = await getCall({ token, liveCallId });
        if (cancelled) return;
        setCall(data);
      } catch (err) {
        if (cancelled) return;
        console.error('[CallRoomPage] getCall error:', err);
        setLoadError(err?.response?.data?.msg || 'Call not found or access denied');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [token, liveCallId]);

  useEffect(() => {
    if (!videoSocket || !liveCallId) return;

    videoSocket.emit('client:video:join', { liveCallId });

    const onChunk = (chunk) => {
      if (String(chunk.liveCallId) !== String(liveCallId)) return;
      if (!chunk.isFinal) return;
      setTranscript((prev) => [...prev, chunk]);
    };
    const onCallEnded = (payload) => {
      if (String(payload.liveCallId) !== String(liveCallId)) return;
      setPostCall({ durationSec: payload.durationSec, transcriptId: payload.transcriptId });
    };
    const onDeclined = (payload) => {
      if (String(payload.liveCallId) !== String(liveCallId)) return;
      setClientDeclined(true);
    };
    const onError = (err) => console.warn('[CallRoomPage] /video error:', err);

    videoSocket.on('server:video:transcript_chunk', onChunk);
    videoSocket.on('server:video:call_ended', onCallEnded);
    videoSocket.on('server:video:call_declined', onDeclined);
    videoSocket.on('server:video:error', onError);

    return () => {
      videoSocket.off('server:video:transcript_chunk', onChunk);
      videoSocket.off('server:video:call_ended', onCallEnded);
      videoSocket.off('server:video:call_declined', onDeclined);
      videoSocket.off('server:video:error', onError);
      videoSocket.emit('client:video:leave', { liveCallId });
    };
  }, [videoSocket, liveCallId]);

  useEffect(() => {
    const onChange = () => setFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  // Spec D — listen for in-session assessment events on the main socket.
  // Client receives 'pushed_to_client' and opens the runner;
  // Therapist receives 'results_ready' and we show a toast with the score.
  useEffect(() => {
    if (!mainSocket || !liveCallId) return;
    const onPushed = (payload) => {
      if (String(payload.liveCallId) !== String(liveCallId)) return;
      if (!isTherapist) {
        setPendingAssessment({
          assessmentKey: payload.assessmentKey,
          assessmentInstanceId: payload.assessmentInstanceId,
          // The server resolves the client's conversation when it pushes the
          // assessment. Without it the submit is rejected (ST-20260916-0006).
          conversationId: payload.conversationId || null
        });
      } else {
        setAssessmentToast(`Assessment sent to client: ${payload.assessmentKey}`);
      }
    };
    const onResults = (payload) => {
      if (String(payload.liveCallId) !== String(liveCallId)) return;
      const sc = (typeof payload.score === 'number') ? `score ${payload.score}` : '';
      const iv = payload.interpretation ? ` — ${payload.interpretation}` : '';
      setAssessmentToast(`${payload.assessmentKey?.toUpperCase() || 'Assessment'} results: ${sc}${iv}`);
    };
    mainSocket.on('server:in_session:assessment_pushed_to_client', onPushed);
    mainSocket.on('server:in_session:assessment_results_ready', onResults);
    return () => {
      mainSocket.off('server:in_session:assessment_pushed_to_client', onPushed);
      mainSocket.off('server:in_session:assessment_results_ready', onResults);
    };
  }, [mainSocket, liveCallId, isTherapist]);

  async function handleAssessmentComplete(submission) {
    const submitted = submission?.submittedResult || submission;
    try {
      await axios.post(
        `${API_BASE_URL}/calls/${liveCallId}/in-session/assessment-completed`,
        {
          assessmentInstanceId: pendingAssessment?.assessmentInstanceId,
          assessmentKey: pendingAssessment?.assessmentKey,
          score: submitted?.score ?? null,
          interpretation: submitted?.interpretation ?? null,
          detail: submitted?.answers || null
        },
        { headers: { 'x-auth-token': token } }
      );
    } catch (err) {
      console.warn('[CallRoomPage] assessment-completed POST failed:', err?.message);
    } finally {
      setPendingAssessment(null);
    }
  }

  const handleJoin = async (payload) => {
    setJoinInFlight(true);
    try {
      if (!isTherapist && payload?.consentCopyVersion) {
        try {
          await recordCallConsent({ token, liveCallId, copyVersion: payload.consentCopyVersion });
        } catch (consentErr) {
          // Log but proceed — don't block joining over consent-persistence failure;
          // the client explicitly consented in the UI before pressing Join.
          console.warn('[CallRoomPage] consent persist failed:', consentErr.message);
        }
      }
      const { token: lkToken } = await joinCall({ token, liveCallId });
      setLivekitToken(lkToken);
    } catch (err) {
      console.error('[CallRoomPage] joinCall error:', err);
      setLoadError(err?.response?.data?.msg || 'Could not join call');
    } finally {
      setJoinInFlight(false);
    }
  };

  const {
    localParticipant,
    remoteParticipants,
    connectionState,
    connectionQuality,
    toggleAudio,
    toggleVideo,
    disconnect
  } = useLiveKitRoom({
    token: livekitToken,
    livekitUrl: LIVEKIT_URL,
    autoConnect: !!livekitToken && !postCall
  });

  const remote = remoteParticipants[0] || null;

  useEffect(() => {
    if (!localParticipant || remote || postCall) {
      setRemoteWaitSeconds(0);
      return undefined;
    }
    const t = setInterval(() => setRemoteWaitSeconds((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [localParticipant, remote, postCall]);

  const remoteUnreachable = isTherapist && !remote && !clientDeclined && remoteWaitSeconds >= 60;

  // Leaving without Hang up. Mid-call, the browser asks before the tab is closed
  // or reloaded; if the therapist's page goes anyway (back button, closed tab)
  // the call is ended rather than left 'active' for days (9 and 15 Sep 2026).
  // Refs, not state: a network blip clears localParticipant and must not end
  // the call, so only a real unmount or page hide counts.
  const leaveRef = useRef({ joined: false, ended: false });
  const isTherapistRef = useRef(false);
  isTherapistRef.current = isTherapist;
  useEffect(() => { if (localParticipant) leaveRef.current.joined = true; }, [localParticipant]);
  useEffect(() => { if (postCall) leaveRef.current.ended = true; }, [postCall]);
  useEffect(() => {
    const endOnLeave = () => {
      const state = leaveRef.current;
      if (!isTherapistRef.current || !state.joined || state.ended) return;
      state.ended = true;
      fetch(`${API_BASE_URL}/calls/${liveCallId}/end`, {
        method: 'POST',
        keepalive: true,
        headers: { 'x-auth-token': token }
      }).catch(() => {});
    };
    const warnBeforeUnload = (event) => {
      const state = leaveRef.current;
      if (!state.joined || state.ended) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warnBeforeUnload);
    window.addEventListener('pagehide', endOnLeave);
    return () => {
      window.removeEventListener('beforeunload', warnBeforeUnload);
      window.removeEventListener('pagehide', endOnLeave);
      endOnLeave();
    };
  }, [liveCallId, token]);

  const handleToggleMic = async () => {
    await toggleAudio();
    setMicEnabled((v) => !v);
  };
  const handleToggleCamera = async () => {
    await toggleVideo();
    setCameraEnabled((v) => !v);
  };
  const handleHangup = async () => {
    leaveRef.current.ended = true;
    try { await disconnect(); } catch (_) { /* noop */ }
    try { await endCall({ token, liveCallId }); } catch (_) { /* noop */ }
    if (!postCall) setPostCall({ durationSec: null });
  };

  const handleToggleFullscreen = async () => {
    try {
      if (!document.fullscreenElement) {
        await containerRef.current?.requestFullscreen?.();
      } else {
        await document.exitFullscreen?.();
      }
    } catch (err) {
      console.warn('[CallRoomPage] fullscreen toggle failed:', err.message);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (loadError) {
    return (
      <Box sx={{ p: 4, maxWidth: 640, mx: 'auto' }}>
        <Alert severity="error">{loadError}</Alert>
        <Button sx={{ mt: 2 }} onClick={() => navigate(-1)}>Back</Button>
      </Box>
    );
  }

  if (postCall) {
    return (
      <>
        <Paper sx={{ p: 4, maxWidth: 640, mx: 'auto', my: 6 }}>
          <Typography variant="h5" gutterBottom>Call ended</Typography>
          {postCall.durationSec != null && (
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Duration: {Math.round(postCall.durationSec)}s
            </Typography>
          )}
          {isTherapist && (
            <Typography variant="body2" sx={{ mt: 2 }}>
              Captured {transcript.length} transcript {transcript.length === 1 ? 'segment' : 'segments'}.
            </Typography>
          )}
          {isTherapist && call?.clientId && (
            <Box sx={{ mt: 3 }}>
              <EnrolmentApprovalPanel
                clientId={call.clientId?._id || call.clientId}
                liveCallId={liveCallId}
                token={token}
              />
            </Box>
          )}
          <Box sx={{ display: 'flex', gap: 1, mt: 3, flexWrap: 'wrap' }}>
            {isTherapist && transcript.length > 0 && (
              <>
                <Button variant="outlined" startIcon={<ArticleIcon />} onClick={() => setReviewOpen(true)}>
                  View transcript and notes
                </Button>
                <Button variant="outlined" onClick={() => setSummaryOpen(true)}>
                  View summary
                </Button>
              </>
            )}
            <Button
              variant="contained"
              onClick={() => {
                if (isTherapist) {
                  navigate('/therapist/dashboard');
                } else {
                  // Therapist-Led / hybrid clients live on /portal, not /chat.
                  const portalUser = user?.effectiveClientUIMode === 'patient_portal';
                  navigate(portalUser ? '/portal' : '/chat');
                }
              }}
            >
              Done
            </Button>
          </Box>
        </Paper>
        <TranscriptReviewDialog
          open={reviewOpen}
          onClose={() => setReviewOpen(false)}
          token={token}
          liveCallId={liveCallId}
          callMeta={{ createdAt: call?.createdAt, durationSec: postCall?.durationSec, clientId: call?.clientId }}
        />
        <CallSummaryDialog
          open={summaryOpen}
          onClose={() => setSummaryOpen(false)}
          token={token}
          liveCallId={liveCallId}
          callMeta={{ createdAt: call?.createdAt, durationSec: postCall?.durationSec }}
        />
      </>
    );
  }

  if (!livekitToken) {
    return (
      <PreJoinLobby
        title={isTherapist ? 'Ready to start the call?' : 'Your therapist is calling'}
        subtitle={isTherapist
          ? 'Check your camera and microphone, then start the call.'
          : 'Check your camera and microphone, then join when you are ready.'}
        joinLabel={isTherapist ? 'Start call' : 'Join call'}
        joining={joinInFlight}
        onJoin={handleJoin}
        requireConsent={!isTherapist}
      />
    );
  }

  const showRightPanel = isTherapist && rightPanel !== 'none';

  return (
    <Box
      ref={containerRef}
      sx={{
        position: 'fixed',
        inset: 0,
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'grey.100',
        // Above the app's own header (drawer + 1), so during a call the only
        // way out is Hang up. The header's logo and links used to sit over the
        // call, and navigating away dropped it (ST-20260915-0002/0003).
        zIndex: (theme) => theme.zIndex.drawer + 2
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1, bgcolor: 'background.paper', borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
          {isTherapist ? 'Video call with your client' : 'Video call with your therapist'}
        </Typography>
        <Button size="small" startIcon={<HelpOutlineIcon fontSize="small" />} onClick={() => setSupportTicketOpen(true)}>
          Report an issue
        </Button>
      </Box>
      <Box sx={{ flex: 1, display: 'flex', minHeight: 0, gap: 1.5, p: 1.5 }}>
        {/* Main video column */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1.5, minWidth: 0 }}>
          <Box sx={{ flex: 1, minHeight: 0, display: 'flex', alignItems: 'stretch', justifyContent: 'center' }}>
            <Box
              sx={{
                width: '100%',
                maxHeight: '100%',
                aspectRatio: '16 / 9',
                position: 'relative'
              }}
            >
              <VideoTile
                participant={remote}
                label={
                  remote
                    ? participantLabel(remote)
                    : (isTherapist ? 'Waiting for your client to join…' : 'Connecting…')
                }
                primary
              />
              <Box sx={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: 0.5 }}>
                {!pipVisible && localParticipant && (
                  <Tooltip title="Show self view">
                    <IconButton
                      size="small"
                      onClick={() => setPipVisible(true)}
                      sx={{ bgcolor: 'rgba(0,0,0,0.55)', color: 'common.white', '&:hover': { bgcolor: 'rgba(0,0,0,0.75)' } }}
                    >
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                {pipVisible && localParticipant && (
                  <Tooltip title="Hide self view">
                    <IconButton
                      size="small"
                      onClick={() => setPipVisible(false)}
                      sx={{ bgcolor: 'rgba(0,0,0,0.55)', color: 'common.white', '&:hover': { bgcolor: 'rgba(0,0,0,0.75)' } }}
                    >
                      <VisibilityOffIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip title={fullscreen ? 'Exit fullscreen' : 'Fullscreen'}>
                  <IconButton
                    size="small"
                    onClick={handleToggleFullscreen}
                    sx={{ bgcolor: 'rgba(0,0,0,0.55)', color: 'common.white', '&:hover': { bgcolor: 'rgba(0,0,0,0.75)' } }}
                  >
                    {fullscreen ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </Box>

          {isTherapist && clientDeclined && !remote && (
            <Alert severity="info" sx={{ mx: 1 }}>
              Your client declined the call. Hang up, and try again at a time that suits them or send them a message.
            </Alert>
          )}

          {remoteUnreachable && (
            <Alert severity="warning" sx={{ mx: 1 }}>
              Your client hasn't joined after a minute. You can keep waiting, or hang up and try again later.
            </Alert>
          )}

          <CallControls
            micEnabled={micEnabled}
            cameraEnabled={cameraEnabled}
            onToggleMic={handleToggleMic}
            onToggleCamera={handleToggleCamera}
            onHangup={handleHangup}
            disabled={!localParticipant}
            extraActions={isTherapist && (
              <InCallAssessmentMenu liveCallId={liveCallId} token={token} />
            )}
          />

          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, pb: 1 }}>
            <ConnectionQualityBadge quality={connectionQuality} connectionState={connectionState} />
            <MuiTooltip title={INCALL_BADGE_TOOLTIP}>
              <Chip
                icon={<EditNoteOutlinedIcon fontSize="small" />}
                label={INCALL_BADGE_LABEL}
                size="small"
                variant="outlined"
                color="default"
              />
            </MuiTooltip>
          </Box>
        </Box>

        {/* Right-side panel (therapist only) */}
        {showRightPanel && (
          <Box sx={{ width: { md: 360, lg: 420 }, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
            <ToggleButtonGroup
              value={rightPanel}
              exclusive
              size="small"
              onChange={(_, next) => next && setRightPanel(next)}
              fullWidth
              sx={{ bgcolor: 'background.paper' }}
            >
              <ToggleButton value="transcript" aria-label="Transcript">
                <ArticleIcon fontSize="small" sx={{ mr: 0.5 }} /> Transcript
              </ToggleButton>
              <ToggleButton value="clinical" aria-label="Client context">
                <InfoOutlinedIcon fontSize="small" sx={{ mr: 0.5 }} /> Client
              </ToggleButton>
              <ToggleButton value="notes" aria-label="Session notes">
                <EditNoteIcon fontSize="small" sx={{ mr: 0.5 }} /> Notes
              </ToggleButton>
            </ToggleButtonGroup>

            <Box sx={{ flex: 1, minHeight: 0 }}>
              {rightPanel === 'transcript' && <TranscriptPane messages={transcript} />}
              {rightPanel === 'clinical' && (
                <CallClinicalPanel
                  clientId={call?.clientId}
                  conversationId={call?.conversationId}
                  liveCallId={liveCallId}
                  token={token}
                />
              )}
              {rightPanel === 'notes' && (
                <CallNotesPane
                  token={token}
                  liveCallId={liveCallId}
                  initialValue={call?.therapistNotes || ''}
                />
              )}
            </Box>
          </Box>
        )}

        {/* Therapist-only toggle to collapse the right panel entirely */}
        {isTherapist && (
          <Tooltip title={showRightPanel ? 'Hide side panel' : 'Show side panel'}>
            <IconButton
              size="small"
              onClick={() => setRightPanel(showRightPanel ? 'none' : 'transcript')}
              sx={{
                position: 'absolute',
                right: 8,
                top: 64,
                bgcolor: 'background.paper',
                boxShadow: 1,
                '&:hover': { bgcolor: 'background.default' }
              }}
            >
              {showRightPanel ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Floating self-view PiP */}
      <DraggablePiP
        visible={!!localParticipant && pipVisible}
        onHide={() => setPipVisible(false)}
      >
        <VideoTile participant={localParticipant} label="You" mirror />
      </DraggablePiP>

      {/* Spec D — In-session toolbar (therapist only, fixed bottom-right) */}
      {isTherapist && (
        <InSessionToolbar
          liveCallId={liveCallId}
          scheduledEnd={call?.scheduledEnd}
          onError={setLoadError}
        />
      )}

      {/* Spec D — In-session assessment runner (client only).
          Wrapped in a Dialog so it overlays the call UI properly with
          its own scrollable content area, instead of being clipped by
          the position-fixed call layout. Cancel only dismisses the
          dialog; the call stays open. */}
      {!isTherapist && pendingAssessment && (
        <Dialog
          open
          onClose={(_e, reason) => {
            // Block accidental dismissal — only the Cancel/Close
            // controls inside the runner end the assessment.
            if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
            setPendingAssessment(null);
          }}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              maxHeight: '90vh',
              display: 'flex',
              flexDirection: 'column',
            },
          }}
        >
          <DialogContent sx={{ p: 0, overflowY: 'auto', flex: 1 }}>
            <AssessmentRunner
              token={token}
              testKey={pendingAssessment.assessmentKey}
              conversationId={pendingAssessment.conversationId}
              initiatedBy="therapist"
              onComplete={handleAssessmentComplete}
              onCloseRequest={() => setPendingAssessment(null)}
            />
          </DialogContent>
        </Dialog>
      )}

      {/* Toast — therapist sees pushed/result confirmation */}
      <Snackbar
        open={!!assessmentToast}
        autoHideDuration={6000}
        onClose={() => setAssessmentToast(null)}
        message={assessmentToast}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      />

      {/* Report-issue dialog for both sides; the client had none
          (ST-20260915-0001). Carries the live call's state so admins can
          correlate the report with LiveKit logs. */}
      {Boolean(call) && (
        <SupportTicketDialog
          open={supportTicketOpen}
          onClose={() => setSupportTicketOpen(false)}
          token={token}
          currentPage={isTherapist ? 'therapist_call' : 'client_call'}
          extraContext={{
            userRole: isTherapist ? 'therapist' : 'client',
            journeyStage: 'therapy_call',
            uiLocation: 'Live video call',
            liveCallId,
            viewingClientId: isTherapist ? (call?.clientId || undefined) : undefined,
            callConnectionState: connectionState || undefined,
            callConnectionQuality: connectionQuality || undefined,
            micEnabled,
            cameraEnabled,
          }}
          reportingFrom="Live video call"
        />
      )}
    </Box>
  );
}
